import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDownRight } from "lucide-react";
import { DEFAULT_WA, scrollToId } from "../../lib/api";
import { MaskedLines } from "../../lib/motion";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      id="top"
      data-testid="hero"
      ref={ref}
      className="relative min-h-screen bg-ink text-paper overflow-hidden grain"
    >
      <div className="absolute inset-0 ledger-bg" />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-crimson/20 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-[72px] grid lg:grid-cols-12 gap-12 items-center min-h-screen">
        <div className="lg:col-span-7 pt-14 lg:pt-8 pb-16">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="inline-flex items-center gap-2 text-[11px] md:text-xs uppercase tracking-[0.28em] text-paper/60 border border-line-dark rounded-full px-4 py-2"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-crimson-bright" />
            Contabiltec · Assessoria Empresarial · Manaus — AM
          </motion.p>

          <h1 className="mt-8 font-display font-extrabold tracking-tightest leading-[0.95] text-5xl sm:text-6xl lg:text-[5.2rem]">
            <MaskedLines
              lines={[
                <span key="a">Sua contabilidade,</span>,
                <span key="b">organizada</span>,
                <span key="c" className="text-crimson-bright">sem complicação.</span>,
              ]}
            />
          </h1>

          <motion.p
            data-testid="hero-subline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 max-w-xl text-base md:text-lg text-paper/70 leading-relaxed"
          >
            Abertura de empresa, regularização de CNPJ e consultoria contábil
            especial para você pagar o imposto certo — e só ele.{" "}
            <span className="text-paper font-semibold">Pré-análise gratuita.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-whatsapp-cta"
              href={DEFAULT_WA}
              target="_blank"
              rel="noreferrer"
              className="btn-sweep group inline-flex items-center gap-3 bg-crimson text-paper font-semibold px-7 py-4 rounded-full transition-transform duration-300 hover:scale-[1.03]"
            >
              <MessageCircle size={18} />
              Fale conosco no WhatsApp
            </a>
            <button
              data-testid="hero-services-cta"
              onClick={() => scrollToId("servicos")}
              className="inline-flex items-center gap-2 border border-line-dark hover:border-paper/50 text-paper font-medium px-7 py-4 rounded-full transition-colors duration-300"
            >
              Ver serviços e valores
              <ArrowDownRight size={18} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-14 pt-8 border-t border-line-dark flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              "Pré-análise gratuita",
              "Conclusão em até 72 horas*",
              "Taxas inclusas nas aberturas",
            ].map((s) => (
              <div key={s} className="flex items-center gap-2 text-sm text-paper/70">
                <span className="w-1.5 h-1.5 rounded-full bg-crimson-bright" />
                {s}
              </div>
            ))}
            <p className="w-full text-[11px] text-paper/40">
              *Conforme documentação e prazos dos órgãos responsáveis.
            </p>
          </motion.div>
        </div>

        <motion.div style={{ y }} className="hidden lg:block lg:col-span-5 relative pb-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-crimson translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden aspect-[4/5] max-h-[62vh]">
              <img
                src="/brand/art-consultoria.jpg"
                alt="Consultoria contábil especial para empresas — Contabiltec"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
            </div>

            <div className="absolute -right-6 -top-8 w-32 h-32 pointer-events-none">
              <div className="relative w-full h-full rounded-full bg-ink border border-line-dark flex items-center justify-center">
                <svg viewBox="0 0 120 120" className="stamp-spin absolute inset-0 w-full h-full">
                  <defs>
                    <path id="circ" d="M60,60 m-42,0 a42,42 0 1,1 84,0 a42,42 0 1,1 -84,0" />
                  </defs>
                  <text fill="#F5EFE6" fontSize="10" letterSpacing="2.5">
                    <textPath href="#circ">PRÉ-ANÁLISE GRATUITA • FALE CONOSCO •</textPath>
                  </text>
                </svg>
                <ArrowDownRight size={22} className="text-crimson-bright" />
              </div>
            </div>

            <div className="float-y absolute -left-10 top-16 bg-ink-2 border border-line-dark rounded-2xl px-5 py-4 shadow-2xl">
              <p className="text-[11px] uppercase tracking-widest text-paper/50">Abertura LTDA</p>
              <p className="font-display font-bold text-xl">R$ 2.500</p>
            </div>
            <div className="float-y-delayed absolute -left-14 bottom-24 bg-crimson rounded-2xl px-5 py-4 shadow-2xl">
              <p className="text-[11px] uppercase tracking-widest text-paper/60">Regularização CNPJ</p>
              <p className="font-display font-bold text-xl">R$ 640</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
